// src/lib/hooks/useSignedStorageUrl.ts
import { useEffect, useRef, useState } from 'react';
import { extractStoragePath, getFreshContractUrl } from '@/lib/utils/contractUrl';

/**
 * Risolve un URL storage SALVATO in DB in un signed URL fresco, generato al
 * momento dell'apertura. Serve per i bucket resi privati (es. `documents`):
 * gli URL pubblici salvati nelle tabelle degli allegati non funzionano più, e
 * vanno rifirmati a lettura.
 *
 * - Se `storedUrl` è vuoto → ritorna `null`.
 * - Se non è un URL storage Supabase (link esterno, Google Drive, …) →
 *   `extractStoragePath` ritorna null e restituiamo l'URL invariato.
 * - Altrimenti estrae bucket+path e genera un signed URL (default 12h).
 *
 * Valore iniziale = l'URL salvato, così il link è già valido per gli esterni e
 * non "lampeggia" vuoto; viene sostituito dal signed appena risolto.
 */
export function useSignedStorageUrl(
  storedUrl: string | null | undefined,
  ttlSeconds = 12 * 60 * 60,
): string | null {
  const [url, setUrl] = useState<string | null>(storedUrl ?? null);
  const generationRef = useRef(0);

  useEffect(() => {
    setUrl(storedUrl ?? null);
    if (!storedUrl) return;

    // Link esterno o non-storage: nessuna firma da fare.
    if (!extractStoragePath(storedUrl)) return;

    const myGen = ++generationRef.current;
    let active = true;
    getFreshContractUrl(storedUrl, ttlSeconds)
      .then((fresh) => {
        if (active && myGen === generationRef.current) setUrl(fresh);
      })
      .catch(() => {
        /* getFreshContractUrl fa già fallback all'URL originale */
      });

    return () => {
      active = false;
    };
  }, [storedUrl, ttlSeconds]);

  return url;
}
